import { BaseClient } from '@carvana/analytics';

export type EventData = Record<string, unknown>;
export type TrackLog = (message: string, payload: EventData) => void;

/**
 * The shape `@carvana/analytics` calls every client method with. BaseClient itself
 * declares these as `(...args: any[])`, so naming the contract here keeps both this
 * class and its subclasses typed without restating `any`.
 */
export type TrackFn = (
  eventName: string,
  eventData?: EventData,
  log?: TrackLog,
  noPublish?: boolean
) => Promise<void>;

/**
 * BaseClient throws "not implemented" for every method a client does not define,
 * which turns an unconfigured tracker into a runtime error inside analytics
 * middleware. Overriding them with no-ops keeps a partial client silent instead.
 *
 * These must stay instance-property arrow functions: BaseClient assigns its own
 * methods as instance properties, and a prototype method on the subclass would be
 * shadowed by the parent's assignment rather than overriding it.
 */
export default class SafeBaseClient extends BaseClient {
  trackEvent: TrackFn = async () => {
    // Intentionally silent — see the class comment.
  };

  trackUser: TrackFn = async () => {
    // Intentionally silent — see the class comment.
  };

  trackConversion: TrackFn = async () => {
    // Intentionally silent — see the class comment.
  };

  trackException: TrackFn = async () => {
    // Intentionally silent — see the class comment.
  };

  trackMetric: TrackFn = async () => {
    // Intentionally silent — see the class comment.
  };

  methodDoesntExist = () => null;
}
